import type { AdminReportDetailDto, AdminReportStatus } from '@carcheck/shared';

/**
 * Acceptance read-out for the report detail drawer (AUT-265).
 *
 * Replaces `reportActions.ts`, which gated the approve / reject /
 * request-revision buttons on a status machine that no longer has those
 * edges. Admin does not decide anything about a report any more — the
 * customer reviews it live and accepts it, and disagreement becomes an
 * appeal. What an adjudicator actually needs off this surface is the ONE
 * fact the tabs do not show anywhere: **did the customer accept, when, and
 * did they accept it or did the 24h timer accept it for them?** An
 * auto-accepted report means nobody ever looked; a customer-accepted one
 * means they looked and agreed. Those two read very differently under an
 * appeal.
 *
 * Derived from `acceptedAt` / `acceptedBy` (stamped by
 * `ReportsService.acceptInternal`) and NOT from `status`: acceptance
 * deliberately does not move the status — a report rests at `submitted`
 * whether or not it was accepted (see
 * `packages/api/src/modules/reports/report-status.ts`).
 *
 * `approved` / `rejected` / `revision_requested` are LEGACY rows written
 * before moderation was retired. Nothing can produce them now; they are
 * surfaced as historical so an adjudicator reading an old report is not
 * left wondering why it says "approved" when no admin can approve.
 */
export type ReportAcceptanceState =
  /** Customer pressed accept. */
  | 'acceptedByCustomer'
  /** The 24h auto-accept job accepted it — nobody objected, nobody looked. */
  | 'acceptedAuto'
  /** Accepted, but `acceptedBy` carried an unexpected value. */
  | 'accepted'
  /** Final, unaccepted — the clock is running on the customer. */
  | 'awaitingCustomer'
  /** Inspector is still writing it; there is no floor yet. */
  | 'draft'
  /** Pre-AUT-265 admin approval. */
  | 'legacyApproved'
  /** Pre-AUT-265 admin rejection. */
  | 'legacyRejected'
  /** Pre-AUT-265 revision request (rows were drained by the migration). */
  | 'legacyRevisionRequested';

export interface ReportAcceptance {
  state: ReportAcceptanceState;
  /**
   * ISO-8601 instant the state is anchored on, or `null` when the state
   * has no meaningful timestamp (a draft, or a legacy row whose
   * moderation timestamp was never persisted).
   */
  at: string | null;
  /** Drives the bar's icon + colour. */
  tone: 'success' | 'waiting' | 'neutral';
}

/** Legacy moderation statuses → their read-out state. */
const LEGACY_STATES: Partial<Record<AdminReportStatus, ReportAcceptanceState>> = {
  approved: 'legacyApproved',
  rejected: 'legacyRejected',
  revision_requested: 'legacyRevisionRequested',
};

/**
 * The fields `describeAcceptance` reads — narrowed so tests can build a
 * stub.
 *
 * Only three are REQUIRED, and that is deliberate (AUT-284). The LIST row
 * (`AdminReportListItemDto`) carries `status` / `acceptedAt` /
 * `submittedAt` but not `acceptedBy` / `approvedAt` / `rejectedAt`, so the
 * queue feeds this same function instead of growing a second, weaker
 * derivation off the raw status enum — which is precisely how the list
 * came to advertise «ждёт заказчика» on reports the customer had already
 * accepted (and that had been paid out and released).
 *
 * A list row that IS accepted therefore resolves to the generic
 * `accepted` state rather than `acceptedByCustomer` / `acceptedAuto`:
 * accepted-vs-waiting is the fact a queue must not get wrong; WHO accepted
 * is a detail-drawer question, and the drawer has the field.
 */
export type ReportAcceptanceInput = Pick<
  AdminReportDetailDto,
  'status' | 'acceptedAt' | 'submittedAt'
> &
  Partial<Pick<AdminReportDetailDto, 'acceptedBy' | 'approvedAt' | 'rejectedAt'>>;

/**
 * Pure read-out of a report's acceptance state. Acceptance wins over
 * status in every case: a legacy `approved` row that the customer then
 * accepted reads as accepted, because that is the fact that matters.
 */
export const describeAcceptance = (detail: ReportAcceptanceInput): ReportAcceptance => {
  if (detail.acceptedAt !== null) {
    if (detail.acceptedBy === 'customer')
      return { state: 'acceptedByCustomer', at: detail.acceptedAt, tone: 'success' };
    if (detail.acceptedBy === 'auto')
      return { state: 'acceptedAuto', at: detail.acceptedAt, tone: 'success' };
    return { state: 'accepted', at: detail.acceptedAt, tone: 'success' };
  }

  const legacy = LEGACY_STATES[detail.status];
  if (legacy !== undefined) {
    const at =
      legacy === 'legacyApproved'
        ? (detail.approvedAt ?? null)
        : legacy === 'legacyRejected'
          ? (detail.rejectedAt ?? null)
          : null;
    return { state: legacy, at, tone: 'neutral' };
  }

  if (detail.status === 'in_progress') return { state: 'draft', at: null, tone: 'neutral' };

  // `submitted`, unaccepted. `submittedAt === finalizedAt` for every row
  // the finalize path wrote (both are stamped from the same `now`), so it
  // is honest to present it as the finalize instant here — unlike on a
  // draft row, where `submittedAt` is only the DB default.
  return { state: 'awaitingCustomer', at: detail.submittedAt, tone: 'waiting' };
};
